import Head from 'next/head';
import { useRouter } from 'next/router';

const MetaTags = ({
  title = "Aptofy | Patented AI Hiring Platform - Carbon-Efficient Recruitment",
  description = "Aptofy is a patented AI hiring platform that cuts carbon emissions by 50–99% while delivering accurate, bias-free, real-time candidate evaluations.",
  keywords = "Aptofy, AI hiring platform, patented recruitment technology, carbon-efficient hiring, bias-free assessments, real-time candidate evaluation, sustainable recruitment, AI interview automation, talent acquisition platform, Intelligent Interviewing, Interview as a Service",
  image = "https://www.aptofy.com/assets/aptofy.png",
  url,
  type = "website",
  noindex = false,
  nofollow = false,
  structuredData = null,
}) => {
  const router = useRouter();
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Check if running on dev server domain (devaptofy.com)
  const isDevServer = typeof window !== 'undefined' && 
    (window.location.hostname === 'devaptofy.com' || 
     window.location.hostname.includes('devaptofy'));

  // Get current URL if not provided
  const currentUrl = url || `https://www.aptofy.com${router.asPath}`;
  
  // Combine title with site name
  const fullTitle = title.includes('Aptofy') ? title : `${title} | Aptofy`;
  
  // Robots directive - block indexing for dev server, development mode, or explicit noindex
  let robotsContent = "index, follow";
  if (isDevelopment || isDevServer || noindex) {
    robotsContent = nofollow ? "noindex, nofollow" : "noindex, follow";
  } else if (nofollow) {
    robotsContent = "index, nofollow";
  }

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Aptofy" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="application-name" content="Aptofy" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Aptofy" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <>
          {Array.isArray(structuredData) ? (
            structuredData.map((data, index) => (
              <script
                key={index}
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
              />
            ))
          ) : (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
            />
          )}
        </>
      )}
    </Head>
  );
};

export default MetaTags;